import React, { useContext, useEffect, useState } from "react";
import { useAuth } from "react-oidc-context";
import { HotelContext } from "../../../context/HotelContext";
import { getLocale, translate } from "../../../utils/Translation";
import LoadingIndicator from "./LoadingIndicator";
import "./HotelUtilityPricesCard.css";

const CLOUD7_ALULA_HID = 37;

export const formatUtilityPrice = (value, currency = "EUR", locale = "fr-FR") => {
  if (value === null || value === undefined) {
    return "—";
  }
  const n = Number(value);
  if (!Number.isFinite(n)) {
    return "—";
  }
  const formatted = n.toLocaleString(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  if (currency === "SAR") {
    return `${formatted} SAR`;
  }
  return `${formatted}€`;
};

export const formatUtilityPriceEuro = (value, locale = "fr-FR") => formatUtilityPrice(value, "EUR", locale);

const HotelUtilityPricesCard = ({ className = "" }) => {
  const keycloak = useAuth();
  const { hId, lang } = useContext(HotelContext);
  const locale = getLocale(lang);
  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [prices, setPrices] = useState({
    waterPricePerM3: null,
    energyPricePerKwh: null,
  });

  useEffect(() => {
    if (!keycloak?.isAuthenticated || !keycloak.user?.access_token || !hId) {
      setLoading(false);
      setHasError(!hId);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setHasError(false);

    fetch(`/api/hotels/${hId}/water/utility-prices`, {
      method: "GET",
      headers: {
        Authorization: "Bearer " + keycloak.user.access_token,
        "x-front-token": "Bs#PSQyhy)!y9Ti{",
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        if (cancelled) return;
        if (data.success !== "OK") {
          throw new Error(data.message || "Fetch failed");
        }
        setPrices({
          waterPricePerM3: data.waterPricePerM3,
          energyPricePerKwh: data.energyPricePerKwh,
        });
        setHasError(false);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("HotelUtilityPricesCard:", err);
        setHasError(true);
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [keycloak?.isAuthenticated, keycloak.user?.access_token, hId]);

  const cardClassName = ["hotel-utility-prices-card", className].filter(Boolean).join(" ");
  const isCloud7AlUla = Number(hId) === CLOUD7_ALULA_HID;

  return (
    <section className={cardClassName} aria-labelledby="hotel-utility-prices-title">
      <h2 id="hotel-utility-prices-title" className="hotel-utility-prices-card__title">
        {translate("v2_hotel_utility_title", lang)}
      </h2>

      {loading ? (
        <div className="hotel-utility-prices-card__loading">
          <LoadingIndicator />
        </div>
      ) : (
        <div className="hotel-utility-prices-card__grid">
          <div className="hotel-utility-prices-card__metric hotel-utility-prices-card__metric--water">
            <p className="hotel-utility-prices-card__label">{translate("v2_hotel_utility_water", lang)}</p>
            <p className="hotel-utility-prices-card__value">
              {hasError
                ? "—"
                : formatUtilityPrice(prices.waterPricePerM3, isCloud7AlUla ? "SAR" : "EUR", locale)}
            </p>
          </div>
          <div className="hotel-utility-prices-card__metric hotel-utility-prices-card__metric--energy">
            <p className="hotel-utility-prices-card__label">{translate("v2_hotel_utility_energy", lang)}</p>
            <p className="hotel-utility-prices-card__value">
              {hasError
                ? "—"
                : formatUtilityPrice(prices.energyPricePerKwh, isCloud7AlUla ? "SAR" : "EUR", locale)}
            </p>
          </div>
        </div>
      )}
    </section>
  );
};

export default HotelUtilityPricesCard;
